import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Sidebar } from "../includes/sidebar";
import { Topbar } from "../includes/topbar";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "../../components/ui/breadcrumb";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/ui/table";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from "../../components/ui/dropdown-menu";
import { MoreHorizontal, Search, Eye, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Wallet, Loader2, AlertCircle, Plus, User, Calendar, DollarSign, TrendingUp, Check, Printer } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { paiementsApi, PaiementSOL, CreatePaiementResponse, PaiementAggregatedResponse } from "../../api/paiements.api";
import { comptesApi, Compte } from "../../api/clients.api";
import { useToast } from "../../hooks/use-toast";

import { PaymentDetailsModal } from "./components/PaymentDetailsModal";

// Utility function to format date
const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-HT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const formatShortDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-HT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

// Utility function to format amount
const formatAmount = (amount: number | undefined | null) => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('fr-HT', {
        style: 'currency',
        currency: 'HTG',
        minimumFractionDigits: 0,
    }).format(amount);
};

export default function PaymentsPage() {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [statusFilter, setStatusFilter] = useState("Tous");

    // API state
    const [paiements, setPaiements] = useState<PaiementSOL[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalCount, setTotalCount] = useState(0);

    // Create modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [newPaiement, setNewPaiement] = useState({ compte_id: '', montant: '' });

    // Account search state
    const [accountSearch, setAccountSearch] = useState("");
    const [searchResults, setSearchResults] = useState<Compte[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState<Compte | null>(null);
    const [aggregatedData, setAggregatedData] = useState<PaiementAggregatedResponse | null>(null);
    const [aggregatedLoading, setAggregatedLoading] = useState(false);

    // Confirmation dialog state
    const [showConfirmation, setShowConfirmation] = useState(false);

    // Receipt modal state
    const [showReceipt, setShowReceipt] = useState(false);
    const [receiptData, setReceiptData] = useState<{
        paiement_id: string;
        client_nom: string;
        compte_code: string;
        montant: number;
        date: string;
        reste_a_peye: number;
        pdf_base64?: string;  // PDF from backend
    } | null>(null);

    // Details modal state
    const [selectedPayment, setSelectedPayment] = useState<PaiementSOL | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);

    // Fetch paiements
    useEffect(() => {
        loadPaiements();
    }, [currentPage, itemsPerPage]);

    const loadPaiements = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await paiementsApi.list({
                page: currentPage,
                page_size: itemsPerPage,
            });
            setPaiements(response.results || []);
            setTotalCount(response.count || 0);
        } catch (err: any) {
            console.error('Error loading paiements:', err);
            setError(err.response?.data?.error || 'Erè nan chaje paiements');
        } finally {
            setLoading(false);
        }
    };

    // Search SOL accounts - uses dedicated /sol-actif endpoint
    const searchAccounts = useCallback(async (query: string) => {
        if (query.length < 2) {
            setSearchResults([]);
            return;
        }
        try {
            setSearchLoading(true);
            // Use dedicated endpoint that hardcodes type=SOL, statut=actif
            const response = await comptesApi.searchActiveSolAccounts(query, 10);
            
            // Filter out clients that were created online and are pending verification
            // (Even if backend filters for account statut=actif, we add client filtering for safety)
            const filteredResults = (response.comptes || []).filter(compte => {
                const client = compte.client;
                if (client?.onlineCreated && client?.statut === 'pending_verification') {
                    return false;
                }
                return true;
            });
            setSearchResults(filteredResults);
        } catch (err) {
            console.error('Error searching accounts:', err);
            setSearchResults([]);
        } finally {
            setSearchLoading(false);
        }
    }, []);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (accountSearch) {
                searchAccounts(accountSearch);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [accountSearch, searchAccounts]);

    // Load aggregated data when account selected
    const handleSelectAccount = async (compte: Compte) => {
        setSelectedAccount(compte);
        setSearchResults([]);
        setAccountSearch('');
        setNewPaiement({ ...newPaiement, compte_id: compte.id });

        // Load paiement aggregated data
        try {
            setAggregatedLoading(true);
            const data = await paiementsApi.getAggregated(compte.id);
            setAggregatedData(data);
        } catch (err) {
            console.error('Error loading aggregated data:', err);
            setAggregatedData(null);
        } finally {
            setAggregatedLoading(false);
        }
    };

    // Reset modal state
    const resetModal = () => {
        setShowCreateModal(false);
        setSelectedAccount(null);
        setAggregatedData(null);
        setNewPaiement({ compte_id: '', montant: '' });
        setAccountSearch('');
        setSearchResults([]);
    };

    // Filter paiements locally
    const filteredPayments = paiements.filter(pay => {
        const matchesSearch = pay.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
            pay.compte_id.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
    });

    const totalPages = Math.ceil(totalCount / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;

    // Initiate paiement - show confirmation dialog
    const handleInitiatePayment = () => {
        if (!newPaiement.compte_id || !newPaiement.montant) {
            toast({ title: "Erè", description: "Tanpri chwazi kont ak montan", type: "error" });
            return;
        }

        // Validate against remaining amount
        if (aggregatedData && Number(newPaiement.montant) > aggregatedData.reste_a_peye) {
            toast({
                title: "Erè",
                description: `Montan depase rès: ${formatAmount(aggregatedData.reste_a_peye)}`,
                type: "error"
            });
            return;
        }

        // Show confirmation dialog
        setShowConfirmation(true);
    };

    // Confirm and create paiement
    const handleConfirmPayment = async () => {
        try {
            setCreateLoading(true);
            setShowConfirmation(false);

            const response: CreatePaiementResponse = await paiementsApi.create({
                compte_id: newPaiement.compte_id,
                montant: Number(newPaiement.montant),
            });

            // Save receipt data (use API response values when available)
            const clientName = response.details?.client_nom ||
                aggregatedData?.client_nom ||
                `${selectedAccount?.prenom || ''} ${selectedAccount?.nom || ''}`.trim() || 'N/A';
            const accountCode = response.details?.compte_code || selectedAccount?.code || '';
            const resteApresPayment = response.details?.reste_a_peye ??
                (aggregatedData ? aggregatedData.reste_a_peye - Number(newPaiement.montant) : 0);

            setReceiptData({
                paiement_id: response.paiement_id || 'N/A',
                client_nom: clientName,
                compte_code: accountCode,
                montant: Number(newPaiement.montant),
                date: new Date().toISOString(),
                reste_a_peye: resteApresPayment,
                pdf_base64: response.pdf_base64,  // PDF from backend
            });

            toast({ title: "Siksè", description: response.message || "Paiement kreye avèk siksè!", type: "success" });

            // Show receipt modal
            setShowCreateModal(false);
            setShowReceipt(true);

            loadPaiements();
        } catch (err: any) {
            console.error('Error creating paiement:', err);
            toast({ title: "Erè", description: err.response?.data?.error || err.response?.data?.montant?.[0] || "Erè nan kreye paiement", type: "error" });
        } finally {
            setCreateLoading(false);
        }
    };

    // Print receipt function - opens PDF in new window for printing
    const handlePrintReceipt = () => {
        if (receiptData?.pdf_base64) {
            // Open PDF in new window for printing
            const pdfWindow = window.open('');
            if (pdfWindow) {
                pdfWindow.document.write(`
                    <html>
                        <head><title>Recu Paiement SOL - ${receiptData.paiement_id}</title></head>
                        <body style="margin:0;padding:0;">
                            <embed 
                                src="data:application/pdf;base64,${receiptData.pdf_base64}" 
                                type="application/pdf" 
                                width="100%" 
                                height="100%"
                                style="min-height:100vh;"
                            />
                        </body>
                    </html>
                `);
                pdfWindow.document.close();
                setTimeout(() => pdfWindow.print(), 500);
            }
        } else {
            // Fallback: print current page
            window.print();
        }
    };

    const handleViewDetails = (payment: PaiementSOL) => {
        setSelectedPayment(payment);
        setShowDetailsModal(true);
    };

    return (
        <div className="flex h-screen overflow-hidden">
            <Sidebar />
            <div className="flex-1 flex flex-col overflow-hidden">
                <Topbar />
                <div className="flex-1 overflow-y-auto space-y-4 p-4 sm:p-6 lg:p-8 pt-4 sm:pt-6">
                    <div className="flex items-center justify-between space-y-2">
                        <Breadcrumb>
                            <BreadcrumbList>
                                <BreadcrumbItem>
                                    <BreadcrumbLink className="capitalize">{t('transactions')}</BreadcrumbLink>
                                </BreadcrumbItem>
                                <BreadcrumbSeparator />
                                <BreadcrumbItem>
                                    <BreadcrumbPage className="capitalize">{t('payments.title')}</BreadcrumbPage>
                                </BreadcrumbItem>
                            </BreadcrumbList>
                        </Breadcrumb>
                    </div>
                    <Card>
                        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:space-y-0 pb-2">
                            <div className="space-y-1">
                                <CardTitle className="text-lg sm:text-xl">{t('payments.title')}</CardTitle>
                                <CardDescription className="text-xs sm:text-sm">
                                    {t('payments.description')}
                                </CardDescription>
                            </div>
                            <Button size="sm" onClick={() => setShowCreateModal(true)} className="w-full sm:w-auto">
                                <Plus className="mr-2 h-4 w-4" /> {t('payments.newPayment')}
                            </Button>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center py-4 gap-2 sm:gap-2 sm:space-x-2">
                                <div className="relative w-full sm:max-w-sm">
                                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder={t('payments.searchPlaceholder')}
                                        className="pl-8"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                                <Select value={statusFilter} onValueChange={setStatusFilter}>
                                    <SelectTrigger className="w-full sm:w-[180px]">
                                        <SelectValue placeholder={t('statusLabel')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Tous">{t('payments.status.all')}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Error State */}
                            {error && (
                                <div className="flex items-center justify-center py-8 text-red-500">
                                    <AlertCircle className="h-5 w-5 mr-2" />
                                    <span>{error}</span>
                                    <Button variant="link" onClick={loadPaiements} className="ml-2">
                                        {t('newTransaction.search.tryAgain')}
                                    </Button>
                                </div>
                            )}

                            {/* Loading State */}
                            {loading && (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                    <span className="ml-2">{t('loading')}</span>
                                </div>
                            )}

                            {/* Table */}
                            {!loading && !error && (
                                <div className="rounded-md border overflow-x-auto">
                                    <Table className="min-w-[600px]">
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>{t('payments.table.client')}</TableHead>
                                                <TableHead className="hidden sm:table-cell">{t('payments.table.nbMain')}</TableHead>
                                                <TableHead>{t('payments.table.amount')}</TableHead>
                                                <TableHead className="hidden md:table-cell">{t('payments.table.date')}</TableHead>
                                                <TableHead className="hidden sm:table-cell">{t('payments.table.status')}</TableHead>
                                                <TableHead className="text-right">{t('payments.table.action')}</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {filteredPayments.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                                        {t('noPayments')}
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                filteredPayments.map((pay) => (
                                                    <TableRow key={pay.id}>
                                                        <TableCell>
                                                            <div className="flex items-center gap-2">
                                                                <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                                                    <User className="h-4 w-4 text-green-600" />
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <p className="font-medium text-sm truncate">{pay.client_nom || 'N/A'}</p>
                                                                    <p className="text-xs text-muted-foreground font-mono">{pay.compte_code || pay.compte_id?.slice(0, 8)}</p>
                                                                </div>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="hidden sm:table-cell">{pay.nb_main}</TableCell>
                                                        <TableCell className="font-bold text-green-600 whitespace-nowrap">{formatAmount(pay.montant)}</TableCell>
                                                        <TableCell className="hidden md:table-cell text-sm text-muted-foreground whitespace-nowrap">{formatDate(pay.date || pay.created_at)}</TableCell>
                                                        <TableCell className="hidden sm:table-cell">
                                                            <Badge variant="outline" className="text-green-600 border-green-600 bg-green-50">
                                                                {t('payments.status.paid')}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild>
                                                                    <Button variant="ghost" className="h-8 w-8 p-0">
                                                                        <span className="sr-only">Open menu</span>
                                                                        <MoreHorizontal className="h-4 w-4" />
                                                                    </Button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent align="end">
                                                                    <DropdownMenuLabel>{t('payments.table.action')}</DropdownMenuLabel>
                                                                    <DropdownMenuSeparator className="bg-primary-500" />
                                                                    <DropdownMenuItem onClick={() => handleViewDetails(pay)}>
                                                                        <Eye className="mr-2 h-4 w-4" />
                                                                        {t('details')}
                                                                    </DropdownMenuItem>
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}

                            {/* Pagination */}
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0 py-4">
                                <div className="flex items-center space-x-2 order-2 sm:order-1">
                                    <p className="text-xs sm:text-sm font-medium hidden sm:block">{t('pagination.rowsPerPage')}</p>
                                    <Select
                                        value={`${itemsPerPage}`}
                                        onValueChange={(value) => {
                                            setItemsPerPage(Number(value));
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <SelectTrigger className="h-8 w-[70px]">
                                            <SelectValue placeholder={itemsPerPage} />
                                        </SelectTrigger>
                                        <SelectContent side="top">
                                            {[5, 10, 20, 50].map((pageSize) => (
                                                <SelectItem key={pageSize} value={`${pageSize}`}>
                                                    {pageSize}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex items-center gap-2 order-1 sm:order-2">
                                    <div className="text-xs sm:text-sm font-medium whitespace-nowrap">
                                        {totalCount > 0 ? (
                                            t('pagination.range', { start: indexOfFirstItem + 1, end: Math.min(indexOfLastItem, totalCount), total: totalCount })
                                        ) : (
                                            "0-0 / 0"
                                        )}
                                    </div>
                                    <div className="flex items-center space-x-1">
                                        <Button
                                            variant="outline"
                                            className="hidden h-8 w-8 p-0 lg:flex"
                                            onClick={() => setCurrentPage(1)}
                                            disabled={currentPage === 1}
                                        >
                                            <span className="sr-only">{t('pagination.first')}</span>
                                            <ChevronsLeft className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            className="h-8 w-8 p-0"
                                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                            disabled={currentPage === 1}
                                        >
                                            <span className="sr-only">{t('pagination.previous')}</span>
                                            <ChevronLeft className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            className="h-8 w-8 p-0"
                                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                            disabled={currentPage === totalPages || totalPages === 0}
                                        >
                                            <span className="sr-only">{t('pagination.next')}</span>
                                            <ChevronRight className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            className="hidden h-8 w-8 p-0 lg:flex"
                                            onClick={() => setCurrentPage(totalPages)}
                                            disabled={currentPage === totalPages || totalPages === 0}
                                        >
                                            <span className="sr-only">{t('pagination.last')}</span>
                                            <ChevronsRight className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Create Paiement Modal */}
            <Dialog open={showCreateModal} onOpenChange={(open) => !open && resetModal()}>
                <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-y-auto p-4 sm:p-6">
                    <DialogHeader>
                        <DialogTitle>{t('payments.modal.title')}</DialogTitle>
                        <DialogDescription>
                            {t('payments.modal.description')}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3">
                        {/* Account Search */}
                        {!selectedAccount ? (
                            <div className="space-y-2">
                                <Label>{t('payments.modal.searchAccount')}</Label>
                                <div className="relative">
                                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder={t('payments.modal.searchPlaceholder')}
                                        className="pl-8"
                                        value={accountSearch}
                                        onChange={(e) => setAccountSearch(e.target.value)}
                                    />
                                    {searchLoading && (
                                        <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin" />
                                    )}
                                </div>

                                {/* Search Results */}
                                {searchResults.length > 0 && (
                                    <div className="border rounded-md max-h-48 overflow-y-auto">
                                        {searchResults.map((compte) => (
                                            <div
                                                key={compte.id}
                                                className="flex items-center justify-between p-3 hover:bg-muted cursor-pointer border-b last:border-b-0"
                                                onClick={() => handleSelectAccount(compte)}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                                                        <User className="h-5 w-5 text-green-600" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium">{compte.nom} {compte.prenom}</p>
                                                        <p className="text-sm text-muted-foreground font-mono">{compte.code}</p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-bold text-green-600">{formatAmount(compte.solde_S || compte.soldeS)}</p>
                                                    <Badge variant="outline" className="text-xs">SOL</Badge>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {accountSearch.length >= 2 && searchResults.length === 0 && !searchLoading && (
                                    <p className="text-sm text-muted-foreground text-center py-4">
                                        {t('payments.modal.noAccountFound')}
                                    </p>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="border rounded-md p-3 bg-muted/30">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <div className="h-9 w-9 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
                                                <User className="h-4 w-4 text-white" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="font-medium text-sm truncate">
                                                    {aggregatedData?.client_nom || `${selectedAccount.prenom || ''} ${selectedAccount.nom || ''}`.trim() || 'Kliyan'}
                                                </p>
                                                <p className="text-xs text-muted-foreground font-mono">{selectedAccount.code}</p>
                                            </div>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 text-xs"
                                            onClick={() => {
                                                setSelectedAccount(null);
                                                setAggregatedData(null);
                                                setNewPaiement({ compte_id: '', montant: '' });
                                            }}
                                        >
                                            {t('newTransaction.account.change')}
                                        </Button>
                                    </div>

                                    {aggregatedLoading ? (
                                        <div className="flex items-center justify-center py-4">
                                            <Loader2 className="h-5 w-5 animate-spin text-green-600" />
                                        </div>
                                    ) : aggregatedData ? (
                                        <div className="mt-3 space-y-2">
                                            {/* Stats Row - Responsive */}
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                                <div className="p-2.5 bg-muted/50 rounded-md">
                                                    <p className="text-[11px] text-muted-foreground uppercase tracking-wide">{t('payments.modal.stats.gross')}</p>
                                                    <p className="font-semibold">{formatAmount(aggregatedData.montant_brut)}</p>
                                                </div>
                                                <div className="p-2.5 bg-muted/50 rounded-md">
                                                    <p className="text-[11px] text-muted-foreground uppercase tracking-wide">{t('payments.modal.stats.fee')} ({aggregatedData.fee_percentage}%)</p>
                                                    <p className="font-semibold text-red-600">-{formatAmount(aggregatedData.frais)}</p>
                                                </div>
                                                <div className="p-2.5 bg-muted/50 rounded-md">
                                                    <p className="text-[11px] text-muted-foreground uppercase tracking-wide">{t('payments.modal.stats.net')}</p>
                                                    <p className="font-semibold text-green-600">{formatAmount(aggregatedData.montant_net)}</p>
                                                </div>
                                                <div className="p-2.5 bg-muted/50 rounded-md">
                                                    <p className="text-[11px] text-muted-foreground uppercase tracking-wide">{t('payments.modal.stats.paid')}</p>
                                                    <p className="font-semibold text-blue-600">{formatAmount(aggregatedData.total_peye)}</p>
                                                </div>
                                            </div>

                                            {/* Calculation Details - Flat Table Style */}
                                            <div className="border rounded-md overflow-hidden text-sm">
                                                <div className="flex items-center justify-between px-3 py-2 bg-muted/30">
                                                    <span className="text-muted-foreground">💰 {t('payments.modal.stats.clientDeposit')}</span>
                                                    <span className="font-mono font-medium">{formatAmount(aggregatedData.solde_S)}</span>
                                                </div>
                                                <div className="flex items-center justify-between px-3 py-2 border-t">
                                                    <span className="text-muted-foreground">📤 {t('payments.modal.stats.clientPaid')}</span>
                                                    <span className="font-mono font-medium text-blue-600">-{formatAmount(aggregatedData.total_peye)}</span>
                                                </div>
                                                <div className={`flex items-center justify-between px-3 py-2 border-t ${aggregatedData.dette_bousanm > 0
                                                    ? 'bg-orange-50'
                                                    : aggregatedData.solde_S < aggregatedData.total_peye
                                                        ? 'bg-red-50'
                                                        : 'bg-green-50'
                                                    }`}>
                                                    <span className="font-medium">
                                                        {aggregatedData.dette_bousanm > 0
                                                            ? `➡️ ${t('payments.modal.stats.bousanmOwes')}`
                                                            : aggregatedData.solde_S < aggregatedData.total_peye
                                                                ? `⚠️ ${t('payments.modal.stats.clientOwes')}`
                                                                : `✅ ${t('payments.modal.stats.balanced')}`}
                                                    </span>
                                                    <span className={`font-mono font-bold ${aggregatedData.dette_bousanm > 0
                                                        ? 'text-orange-600'
                                                        : aggregatedData.solde_S < aggregatedData.total_peye
                                                            ? 'text-red-600'
                                                            : 'text-green-600'
                                                        }`}>
                                                        {formatAmount(Math.abs(aggregatedData.solde_S - aggregatedData.total_peye))}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Remaining - Highlighted */}
                                            <div className="flex items-center justify-between p-3 bg-green-600 text-white rounded-md">
                                                <div>
                                                    <p className="text-green-100 text-xs">{t('payments.modal.stats.remaining')}</p>
                                                    <p className="font-bold text-xl">{formatAmount(aggregatedData.reste_a_peye)}</p>
                                                </div>
                                                <Badge variant="secondary" className="bg-white/20 text-white border-0">
                                                    {aggregatedData.statut}
                                                </Badge>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="mt-4 grid grid-cols-2 gap-3">
                                            <div className="bg-white rounded-lg p-3 border">
                                                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                                                    <Calendar className="h-4 w-4" />
                                                    <span>Dire</span>
                                                </div>
                                                <p className="font-bold">{selectedAccount.duree_sol || selectedAccount.duree} jou</p>
                                            </div>
                                            <div className="bg-white rounded-lg p-3 border">
                                                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                                                    <DollarSign className="h-4 w-4" />
                                                    <span>Montan/Jou</span>
                                                </div>
                                                <p className="font-bold">{formatAmount(selectedAccount.montant_jours || selectedAccount.montantJours)}</p>
                                            </div>
                                            <div className="col-span-2 bg-white rounded-lg p-3 border">
                                                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                                                    <Wallet className="h-4 w-4" />
                                                    <span>Balans SOL</span>
                                                </div>
                                                <p className="font-bold text-xl text-green-600">{formatAmount(selectedAccount.solde_S || selectedAccount.soldeS)}</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Amount Input */}
                                    <div className="space-y-2">
                                        <Label htmlFor="montant">{t('payments.modal.inputs.amount')}</Label>
                                        <div className="relative">
                                            <DollarSign className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                id="montant"
                                                type="number"
                                                placeholder={aggregatedData ? t('payments.modal.inputs.amountPlaceholder', { amount: aggregatedData.reste_a_peye }) : t('payments.modal.inputs.amountHtgPlaceholder')}
                                                className="pl-8 text-lg font-bold"
                                                value={newPaiement.montant}
                                                onChange={(e) => setNewPaiement({ ...newPaiement, montant: e.target.value })}
                                            />
                                        </div>
                                        {aggregatedData && newPaiement.montant && Number(newPaiement.montant) > aggregatedData.reste_a_peye && (
                                            <p className="text-sm text-red-500">
                                                ⚠️ Montan depase rès: {formatAmount(aggregatedData.reste_a_peye)}
                                            </p>
                                        )}
                                        {aggregatedData && (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setNewPaiement({ ...newPaiement, montant: String(aggregatedData.reste_a_peye) })}
                                            >
                                                Peye tout rès ({formatAmount(aggregatedData.reste_a_peye)})
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={resetModal}>
                            Anile
                        </Button>
                        <Button
                            onClick={handleInitiatePayment}
                            disabled={createLoading || !selectedAccount || !newPaiement.montant}
                        >
                            {createLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Kreye Paiement
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Confirmation Dialog */}
            <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
                <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader>
                        <DialogTitle>Konfime Paiement</DialogTitle>
                        <DialogDescription>
                            Tanpri verifye detay paiement sa a anvan ou kontinye.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3 py-4">
                        {selectedAccount && aggregatedData && (
                            <>
                                <div className="border rounded-md p-3 bg-muted/30">
                                    <p className="text-sm text-muted-foreground">Kliyan</p>
                                    <p className="font-medium">
                                        {aggregatedData.client_nom || `${selectedAccount.prenom || ''} ${selectedAccount.nom || ''}`.trim()}
                                    </p>
                                    <p className="text-xs text-muted-foreground font-mono">{selectedAccount.code}</p>
                                </div>

                                <div className="border rounded-md p-3 bg-green-50 text-center">
                                    <p className="text-sm text-muted-foreground">Montan pou Peye</p>
                                    <p className="font-bold text-2xl text-green-600">
                                        {formatAmount(Number(newPaiement.montant))}
                                    </p>
                                </div>

                                <div className="text-xs text-muted-foreground text-center">
                                    Apre paiement sa a, rès pou peye: {formatAmount(aggregatedData.reste_a_peye - Number(newPaiement.montant))}
                                </div>
                            </>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowConfirmation(false)}>
                            Anile
                        </Button>
                        <Button onClick={handleConfirmPayment} disabled={createLoading}>
                            {createLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            ✓ Konfime
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Receipt Modal */}
            <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
                <DialogContent className="sm:max-w-[400px] print:max-w-full print:shadow-none">
                    <DialogHeader className="sr-only">
                        <DialogTitle>Recu Paiement SOL</DialogTitle>
                        <DialogDescription>Recu pou paiement SOL</DialogDescription>
                    </DialogHeader>
                    <div id="receipt-content" className="print:p-0">
                        {/* Header */}
                        <div className="text-center border-b pb-4 mb-4">
                            <h2 className="text-2xl font-bold text-green-600">BOUSANM</h2>
                            <p className="text-sm text-muted-foreground">Recu Paiement SOL</p>
                        </div>

                        {receiptData && (
                            <div className="space-y-4">
                                {/* Receipt Number */}
                                <div className="text-center">
                                    <p className="text-xs text-muted-foreground">Recu #</p>
                                    <p className="font-mono text-sm">{receiptData.paiement_id}</p>
                                </div>

                                {/* Client Info */}
                                <div className="border rounded-md p-3 bg-muted/30">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <p className="text-xs text-muted-foreground">Kliyan</p>
                                            <p className="font-medium">{receiptData.client_nom}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-xs text-muted-foreground">Kont</p>
                                            <p className="font-mono text-sm">{receiptData.compte_code}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Amount */}
                                <div className="border-2 border-green-500 rounded-md p-4 bg-green-50 text-center">
                                    <p className="text-sm text-muted-foreground">Montan Peye</p>
                                    <p className="font-bold text-3xl text-green-600">
                                        {formatAmount(receiptData.montant)}
                                    </p>
                                </div>

                                {/* Details */}
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div className="border rounded-md p-2">
                                        <p className="text-xs text-muted-foreground">Dat</p>
                                        <p className="font-medium">{formatDate(receiptData.date)}</p>
                                    </div>
                                    <div className="border rounded-md p-2">
                                        <p className="text-xs text-muted-foreground">Rès pou Peye</p>
                                        <p className="font-medium text-orange-600">{formatAmount(receiptData.reste_a_peye)}</p>
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="text-center text-xs text-muted-foreground border-t pt-4">
                                    <p>Mèsi pou konfyans ou!</p>
                                    <p className="mt-1">bousanm.com</p>
                                </div>
                            </div>
                        )}

                        {/* Print Buttons - Hidden when printing */}
                        <div className="flex gap-2 mt-6 print:hidden">
                            <Button
                                variant="outline"
                                className="flex-1"
                                onClick={() => {
                                    setShowReceipt(false);
                                    resetModal();
                                }}
                            >
                                Fèmen
                            </Button>
                            <Button
                                className="flex-1 bg-green-600 hover:bg-green-700"
                                onClick={handlePrintReceipt}
                            >
                                <Printer className="mr-2 h-4 w-4" />
                                Enprime
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Details Modal */}
            <PaymentDetailsModal
                isOpen={showDetailsModal}
                onClose={() => setShowDetailsModal(false)}
                paiement={selectedPayment}
                onPrint={() => window.print()}
            />

            {/* Print Styles */}
            <style>{`
                @media print {
                    body * { visibility: hidden; }
                    #receipt-content, #receipt-content * { visibility: visible; }
                    #receipt-content { 
                        position: absolute; 
                        left: 50%; 
                        top: 0; 
                        transform: translateX(-50%);
                        width: 80mm;
                        padding: 10mm;
                    }
                }
            `}</style>
        </div >
    );
}
